import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Flex,
  Text,
  Image,
  IconButton,
  Button,
  DrawerHeader,
} from "@chakra-ui/react";
import { TransactionEvent } from "@syla/shared/types/responses/TransactionEvent";
import React from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { FiEdit2 } from "react-icons/fi";
import { EditTransactionForm } from "../../../../../types/order/addOrderForm";
import { SourceDataButton } from "./SourceDataButton";

type TransactionOverviewHeaderProps = {
  icon: string | undefined;
  text: string;
  transaction: TransactionEvent;
  editMode: boolean;
  updating: boolean;
  onClickBack: () => void;
  onOpenEditMode: () => void;
  onCloseEditMode: () => void;
};

export const TransactionOverviewHeader = React.memo(
  function TransactionOverviewHeader({
    icon,
    text,
    transaction,
    editMode,
    updating,
    onClickBack,
    onOpenEditMode,
    onCloseEditMode,
  }: TransactionOverviewHeaderProps): React.ReactElement {
    const { control } = useFormContext<EditTransactionForm>();

    const { isDirty } = useFormState({
      control,
    });

    return (
      <DrawerHeader borderBottomWidth="1px" px={4} py={4}>
        <Flex align="center" justify="space-between" gap={4}>
          <Flex align="center" gap={1} minW={0}>
            <IconButton
              variant="ghost"
              fontSize="1.5rem"
              icon={<ArrowBackIcon />}
              isDisabled={updating}
              onClick={onClickBack}
              aria-label="Back"
            />

            <Flex
              align="center"
              gap={1}
              minW={0}
              display={{ base: "none", md: "flex" }}
            >
              <Image src={icon} alt="" h="2rem" w="2rem" flexShrink={0} />
              <Text noOfLines={1}>{text}</Text>
            </Flex>
          </Flex>

          <TransactionOverviewHeaderActions
            transaction={transaction}
            editMode={editMode}
            updating={updating}
            hasChanges={isDirty}
            onOpenEditMode={onOpenEditMode}
            onCloseEditMode={onCloseEditMode}
          />
        </Flex>
      </DrawerHeader>
    );
  },
);

function TransactionOverviewHeaderActions({
  transaction,
  editMode,
  updating,
  hasChanges,
  onOpenEditMode,
  onCloseEditMode,
}: {
  transaction: TransactionEvent;
  editMode: boolean;
  updating: boolean;
  hasChanges: boolean;
  onOpenEditMode: () => void;
  onCloseEditMode: () => void;
}): React.ReactElement {
  return (
    <Flex gap={4} flexShrink={0}>
      <SourceDataButton transaction={transaction} />

      {editMode ?
        <>
          {hasChanges && (
            <Button
              form="transaction-overview-form"
              key="save"
              variant="solid"
              colorScheme="primary"
              type="submit"
              isDisabled={updating}
              isLoading={updating}
            >
              Save
            </Button>
          )}

          <Button
            key="cancel"
            onClick={onCloseEditMode}
            variant="ghost"
            colorScheme="red"
            isDisabled={updating}
          >
            Cancel
          </Button>
        </>
      : <Button
          key="edit"
          variant="solid"
          colorScheme="primary"
          leftIcon={<FiEdit2 />}
          onClick={onOpenEditMode}
          isDisabled={updating}
          isLoading={updating}
        >
          Edit
        </Button>
      }
    </Flex>
  );
}
